// Fokus och titel vid sidbyte (WCAG 2.4.2 och 2.4.3). När sökvägen ändras flyttas fokus till sidans
// huvudrubrik, så att skärmläsare läser upp den nya sidan och tangentbordet börjar uppifrån.
// Vid första laddningen rörs fokus inte. Har sidan själv flyttat fokus (till exempel till snabbinmatningen
// vid ?ny=1) får den behålla det.
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { pageTitle } from "../lib/title.ts";

/** Titlar för publika sidor som inte sätter titeln själva. */
const PUBLIC_TITLES: [RegExp, string][] = [
  [/^\/logga-in\/?$/u, "Logga in"],
  [/^\/skapa-konto\/?$/u, "Skapa konto"],
  [/^\/glomt-losenord\/?$/u, "Glömt lösenord"],
  [/^\/aterstall\//u, "Välj nytt lösenord"],
  [/^\/inbjudan\//u, "Inbjudan"],
];

/** Hur länge vi väntar på att den nya sidans rubrik ska ritas (den kan ladda data först). */
const WAIT_MS = 3000;

export function RouteFocus() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const first = useRef(true);

  useEffect(() => {
    const t = PUBLIC_TITLES.find(([re]) => re.test(pathname));
    if (t) document.title = pageTitle(t[1]);
  }, [pathname]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ska köras vid varje ny sökväg, även om sökvägen inte används i effekten.
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const before = document.activeElement;
    const started = Date.now();
    let frame = 0;
    let cancelled = false;

    const tryFocus = () => {
      if (cancelled) return;
      const active = document.activeElement;
      const main = document.getElementById("main");
      // Sidan har själv flyttat fokus till något i innehållet: låt det vara.
      if (active && active !== before && active !== document.body && main?.contains(active) && active !== main) return;
      const h1 = main?.querySelector<HTMLElement>("h1") ?? document.querySelector<HTMLElement>("h1");
      if (h1) {
        if (!h1.hasAttribute("tabindex")) h1.setAttribute("tabindex", "-1");
        h1.classList.add("route-focus");
        h1.focus({ preventScroll: true });
        return;
      }
      if (Date.now() - started < WAIT_MS) {
        frame = requestAnimationFrame(tryFocus);
        return;
      }
      main?.focus({ preventScroll: true });
    };
    frame = requestAnimationFrame(tryFocus);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [pathname]);

  return null;
}
